import type { LiveUpdatePlugin } from "@capawesome/capacitor-live-update";

// Shared with the auth flow to delay login while the WebView reloads.
const LIVE_UPDATE_JUST_RELOADED_KEY = "__clovis_live_update_just_reloaded";

function isStagingLiveUpdateChannel(channel: string) {
  return channel === "staging" || /^pr-[1-9]\d*$/.test(channel);
}

function getStartupLiveUpdateChannel(
  appId: string | undefined,
  currentChannel: string | null
): string | null {
  if (appId === "app.clovis.app") return "prod";
  if (appId === "dev.clovis.app") {
    return currentChannel && isStagingLiveUpdateChannel(currentChannel)
      ? currentChannel
      : "staging";
  }
  return currentChannel;
}

// Require both native identity and the loaded bundle to identify staging.
// An unknown app id or production bundle must never expose the test selector.
function canSelectStagingLiveUpdateChannel(
  appId: string | undefined,
  isProductionBundle: boolean | undefined
) {
  return appId === "dev.clovis.app" && isProductionBundle === false;
}

class LiveUpdateChannelError extends Error {
  constructor(readonly reason: "restore-failed" | "unavailable") {
    super(`LiveUpdate channel switch: ${reason}`);
    this.name = "LiveUpdateChannelError";
  }
}

type ChannelUpdater = Pick<
  LiveUpdatePlugin,
  | "addListener"
  | "fetchLatestBundle"
  | "getChannel"
  | "getCurrentBundle"
  | "getNextBundle"
  | "reload"
  | "setChannel"
  | "setNextBundle"
  | "sync"
>;

async function switchLiveUpdateChannel(
  updater: ChannelUpdater,
  channel: string,
  onProgress: (progress: number) => void,
  beforeReload: () => void
): Promise<string> {
  if (!isStagingLiveUpdateChannel(channel)) {
    throw new LiveUpdateChannelError("unavailable");
  }

  const [previousChannel, currentBundle, previousNextBundle] =
    await Promise.all([
      updater.getChannel(),
      updater.getCurrentBundle(),
      updater.getNextBundle(),
    ]);
  const listener = await updater.addListener(
    "downloadBundleProgress",
    (event) => onProgress(Math.round(event.progress * 100))
  );

  try {
    const { nextBundleId } = await updater.sync({ channel });
    let targetBundleId = nextBundleId;
    if (!targetBundleId) {
      // sync returns null both for an up-to-date bundle and an empty channel.
      const latest = await updater.fetchLatestBundle({ channel });
      if (!latest.bundleId || latest.bundleId !== currentBundle.bundleId) {
        throw new LiveUpdateChannelError("unavailable");
      }
      targetBundleId = latest.bundleId;
    }

    // Also clear a stale pending update when this channel is already current.
    await updater.setNextBundle({ bundleId: targetBundleId });
    await updater.setChannel({ channel });
    if (targetBundleId !== currentBundle.bundleId) {
      beforeReload();
      await updater.reload();
    }
    return targetBundleId;
  } catch (error) {
    // sync may already have selected a bundle for the next launch.
    const restored = await Promise.allSettled([
      updater.setNextBundle({ bundleId: previousNextBundle.bundleId }),
      updater.setChannel({ channel: previousChannel.channel }),
    ]);
    if (restored.some((result) => result.status === "rejected")) {
      console.error("Unable to restore LiveUpdate selection", error, restored);
      throw new LiveUpdateChannelError("restore-failed");
    }
    throw error;
  } finally {
    await listener.remove().catch((error) => {
      console.warn("Unable to remove LiveUpdate progress listener", error);
    });
  }
}

export {
  canSelectStagingLiveUpdateChannel,
  getStartupLiveUpdateChannel,
  isStagingLiveUpdateChannel,
  LIVE_UPDATE_JUST_RELOADED_KEY,
  LiveUpdateChannelError,
  switchLiveUpdateChannel,
};
